/**
 * PlatformProviderCard Component
 *
 * Displays a platform LLM provider with health status and actions.
 * Aligned with backend ProviderStatus enum.
 */

import { RefreshCw, Settings, Layers, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';
import { useTriggerHealthCheck } from '../hooks/usePlatformProviders';
import type { PlatformProvider } from '../types';
import { PROVIDER_METADATA } from '../types';
import { getProviderStatusStyle } from '../lib/statusStyles';
import { ProviderIcon } from './ProviderIcons';

interface PlatformProviderCardProps {
  provider: PlatformProvider;
  onEdit: () => void;
  onManageModels: () => void;
  onManagePolicy?: () => void;
}

export function PlatformProviderCard({
  provider,
  onEdit,
  onManageModels,
  onManagePolicy,
}: PlatformProviderCardProps) {
  const healthCheckMutation = useTriggerHealthCheck();
  const { t } = useI18n();

  const metadata = PROVIDER_METADATA[provider.provider_type] || {
    displayName: provider.provider_type,
    icon: '🤖',
    iconBg: 'bg-gray-100 dark:bg-gray-700',
  };

  const statusStyle = getProviderStatusStyle(provider.status);

  const handleHealthCheck = () => {
    healthCheckMutation.mutate(provider.id);
  };

  // Format last check time
  const lastCheckLabel = provider.last_health_check
    ? new Date(provider.last_health_check).toLocaleString()
    : t('admin.llmConfig.never');

  return (
    <div
      className={cn(
        'p-4 bg-white dark:bg-gray-800 rounded-lg border transition-all',
        provider.is_active
          ? 'border-gray-200 dark:border-gray-700'
          : 'border-gray-300 dark:border-gray-600 opacity-60'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Icon + Info */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center',
              metadata.iconBg
            )}
          >
            <ProviderIcon
              providerType={provider.provider_type}
              size={22}
              className="w-[22px] h-[22px] object-contain"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                {provider.display_name}
              </h3>
              {/* Health status badge */}
              <span
                className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  statusStyle.bg,
                  statusStyle.text
                )}
              >
                {t(statusStyle.labelKey)}
              </span>
              {!provider.is_active && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                  {t('admin.llmConfig.validation.inactive')}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
              {/* Capability badges */}
              {provider.supports_streaming && (
                <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  {t('admin.llmConfig.capabilities.streaming')}
                </span>
              )}
              {provider.supports_tools && (
                <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  {t('admin.llmConfig.capabilities.tools')}
                </span>
              )}
              {provider.supports_vision && (
                <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  {t('admin.llmConfig.capabilities.vision')}
                </span>
              )}
              {provider.supports_embeddings && (
                <span className="px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                  {t('admin.llmConfig.capabilities.embeddings')}
                </span>
              )}
            </div>

            {/* Health metadata row */}
            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
              <span>{t('admin.llmConfig.lastCheck', { time: lastCheckLabel })}</span>
            </div>
            {/* ISS-423: se muestra el error del proveedor, no el contador de
                fallos consecutivos (detalle operativo interno irrelevante
                para quien configura el proveedor). */}
            {provider.last_health_error && (
              <p className="mt-1 text-xs text-red-500 truncate" title={provider.last_health_error}>
                {provider.last_health_error}
              </p>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleHealthCheck}
            disabled={healthCheckMutation.isPending}
            title="Run health check"
          >
            <RefreshCw
              className={cn(
                'w-4 h-4',
                healthCheckMutation.isPending && 'animate-spin'
              )}
            />
            <span className="ml-1 hidden sm:inline">{t('common.health')}</span>
          </Button>

          <Button variant="outline" size="sm" onClick={onEdit} title={t('common.edit')}>
            <Settings className="w-4 h-4" />
            <span className="ml-1 hidden sm:inline">{t('common.edit')}</span>
          </Button>

          <Button variant="outline" size="sm" onClick={onManageModels} title={t('common.models')}>
            <Layers className="w-4 h-4" />
            <span className="ml-1 hidden sm:inline">{t('common.models')}</span>
          </Button>

          {onManagePolicy && (
            <Button variant="outline" size="sm" onClick={onManagePolicy} title={t('common.policy')}>
              <SlidersHorizontal className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">{t('common.policy')}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Health check result banner */}
      {healthCheckMutation.isSuccess && healthCheckMutation.data && (
        <div
          className={cn(
            'mt-3 p-2 rounded-lg text-sm',
            healthCheckMutation.data.overall_status === 'healthy'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : healthCheckMutation.data.overall_status === 'degraded'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          )}
        >
          <span className="font-medium capitalize">{healthCheckMutation.data.overall_status}</span>
          {healthCheckMutation.data.latency_ms != null && (
            <span className="ml-2 text-xs opacity-75">({healthCheckMutation.data.latency_ms}ms)</span>
          )}
          {healthCheckMutation.data.error && (
            <span className="ml-2 text-xs">{healthCheckMutation.data.error}</span>
          )}
          {healthCheckMutation.data.models.length > 0 && (
            <span className="ml-2 text-xs">
              — {t('admin.llmConfig.modelsAvailable', { available: healthCheckMutation.data.models.filter((m) => m.status === 'available').length, total: healthCheckMutation.data.models.length })}
            </span>
          )}
        </div>
      )}

      {healthCheckMutation.isError && (
        <div className="mt-3 p-2 rounded-lg text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
          {t('admin.llmConfig.healthCheckFailed', { error: (healthCheckMutation.error as Error)?.message || 'Unknown error' })}
        </div>
      )}
    </div>
  );
}
